import React from "react";
import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon, EditIcon } from "@chakra-ui/icons";
import { AiImageStudioDrawer } from "./AiImageStudioDrawer";
import { AiTextGeneratorModal } from "./AiTextGeneratorModal";
import {
  EntityAiToolkitProps,
  GenerateTextOutput,
  AiAsset,
} from "./types/ai.types";

export const EntityAiToolkit: React.FC<EntityAiToolkitProps> = ({
  entityType,
  entityId,
  title,
  description,
  price,
  location,
  initialFields,
  onApplyText,
  onApplyImage,
}) => {
  const textModal = useDisclosure();
  const imageDrawer = useDisclosure();

  const buttonBg = useColorModeValue("teal.500", "teal.400");
  const buttonColor = useColorModeValue("white", "gray.900");
  const buttonHoverBg = useColorModeValue("teal.600", "teal.300");
  const buttonActiveBg = useColorModeValue("teal.700", "teal.200");
  const buttonShadow = useColorModeValue("sm", "0 0 0 1px rgba(56, 178, 172, 0.25)");

  const menuBg = useColorModeValue("white", "gray.800");
  const menuBorder = useColorModeValue("gray.200", "gray.700");
  const menuShadow = useColorModeValue("xl", "dark-lg");

  const menuItemText = useColorModeValue("gray.800", "whiteAlpha.900");
  const menuItemMutedText = useColorModeValue("gray.600", "gray.300");
  const menuItemHoverBg = useColorModeValue("teal.50", "whiteAlpha.100");
  const menuItemActiveBg = useColorModeValue("teal.100", "whiteAlpha.200");

  const helperText = useColorModeValue("gray.500", "gray.400");

  const iconTextBg = useColorModeValue("white", "whiteAlpha.100");
  const iconTextBorder = useColorModeValue("gray.100", "whiteAlpha.200");

  const handleApplyText = (result: GenerateTextOutput) => {
    onApplyText?.(result);
    textModal.onClose();
  };

  const handleApplyImage = (asset: AiAsset) => {
    onApplyImage?.(asset);
  };

  return (
    <>
      <Menu placement="bottom-start">
        <MenuButton
          as={Button}
          size="sm"
          leftIcon={<EditIcon />}
          rightIcon={<ChevronDownIcon />}
          bg={buttonBg}
          color={buttonColor}
          _hover={{ bg: buttonHoverBg }}
          _active={{ bg: buttonActiveBg }}
          _focusVisible={{
            boxShadow: "outline",
          }}
          borderRadius="full"
          boxShadow={buttonShadow}
        >
          AI Tools
        </MenuButton>

        <Portal>
          <MenuList
            bg={menuBg}
            color={menuItemText}
            borderColor={menuBorder}
            boxShadow={menuShadow}
            borderRadius="xl"
            p={2}
            minW="320px"
          >
            <MenuItem
              borderRadius="lg"
              bg="transparent"
              color={menuItemText}
              onClick={textModal.onOpen}
              _hover={{ bg: menuItemHoverBg }}
              _focus={{ bg: menuItemHoverBg }}
              _active={{ bg: menuItemActiveBg }}
            >
              <HStack spacing={3} align="start">
                <Text
                  w="28px"
                  h="28px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  bg={iconTextBg}
                  borderWidth="1px"
                  borderColor={iconTextBorder}
                  flexShrink={0}
                >
                  ✨
                </Text>

                <Text fontWeight="medium" color={menuItemText}>
                  Generate title, description & captions
                </Text>
              </HStack>
            </MenuItem>

            <MenuItem
              borderRadius="lg"
              bg="transparent"
              color={menuItemText}
              onClick={imageDrawer.onOpen}
              _hover={{ bg: menuItemHoverBg }}
              _focus={{ bg: menuItemHoverBg }}
              _active={{ bg: menuItemActiveBg }}
            >
              <HStack spacing={3} align="start">
                <Text
                  w="28px"
                  h="28px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  bg={iconTextBg}
                  borderWidth="1px"
                  borderColor={iconTextBorder}
                  flexShrink={0}
                >
                  🖼️
                </Text>

                <Text fontWeight="medium" color={menuItemText}>
                  Edit images / remove background
                </Text>
              </HStack>
            </MenuItem>

            <Text px={3} pt={2} fontSize="xs" color={helperText}>
              Works with products, accommodation, car hire and hikes.
            </Text>

            <Text px={3} pt={1} fontSize="xs" color={menuItemMutedText}>
              Use OpenAI or the no-AI fallback depending on your settings.
            </Text>
          </MenuList>
        </Portal>
      </Menu>

      <AiTextGeneratorModal
        isOpen={textModal.isOpen}
        onClose={textModal.onClose}
        entityType={entityType}
        entityId={entityId}
        title={title}
        description={description}
        price={price}
        location={location}
        initialFields={initialFields}
        onApply={handleApplyText}
      />

      <AiImageStudioDrawer
        isOpen={imageDrawer.isOpen}
        onClose={imageDrawer.onClose}
        entityType={entityType}
        entityId={entityId}
        title={title}
        description={description}
        price={price}
        location={location}
        initialFields={initialFields}
        onApplyImage={handleApplyImage}
      />
    </>
  );
};